import React, { useMemo } from "react";
import "./style.scss";
import { format } from "date-fns";
import { useDispatch } from "react-redux";
import { MapPin, Link2, Coins, CreditCard, Users, Clock } from "lucide-react";
import { MODAL_TOGGLE } from "../../../store/Modal/Modal.constant";
import ImagePreview from "../../../modules/ui/ImagePreview";
import OrderInformation from "../../../modules/ui/OrderInformation";

const ShareIcon = ({ shareLink }) => {
  return (
    <div className="orders__right">
      <button type="button" onClick={shareLink} className="share-link-btn">
        <Link2 size={16} />
        Share link
      </button>
    </div>
  );
};

const Invitation = ({
  user_id,
  groupOrders,
  settings,
  isJoin,
  balanceTopUpAmount,
  total_topup,
  hideEdit,
  isManager,
  consumed_budget,
  SubTotal,
  totalPaidByEmployees,
}) => {
  const dispatch = useDispatch();
  const presets = groupOrders?.preset;
  const shareLink = () => {
    dispatch({
      type: MODAL_TOGGLE,
      payload: { data: "isModalShareLink", user_id: user_id },
    });
  };
  const outsideFromBudget = useMemo(
    () => (settings?.budget > 0 ? (settings?.budget / 100).toFixed(2) : 0),
    [settings?.budget]
  );
  const perPersonBudget = useMemo(
    () =>
      groupOrders?.tax_info?.currency +
        groupOrders?.per_person_budget?.cents / 100 || 0,
    [groupOrders?.per_person_budget?.cents]
  );
  const orderDeadline = groupOrders?.order_deadline
    ? new Date(groupOrders.order_deadline * 1000)
    : null;

  const formattedDate = orderDeadline
    ? format(orderDeadline, "EEEE, MMMM do, h:mm a")
    : "loading...";

  const isToday =
    orderDeadline &&
    format(orderDeadline, "EEEE, MMMM do") ===
      format(new Date(), "EEEE, MMMM do");

  const managerData = [
    {
      id: 1,
      type: "Manager",
      icon: Coins,
      iconBg: "#FDEBD7",
      title: "Total paid by company:",
      content: (groupOrders?.tax_info?.currency || "") + (consumed_budget / 100 || 0),
    },
    {
      id: 2,
      type: "Manager",
      icon: CreditCard,
      iconBg: "#E3F3E5",
      title: "Total paid by employees:",
      content:
        (groupOrders?.tax_info?.currency || "") + (totalPaidByEmployees / 100 || 0),
    },
    {
      id: 3,
      type: "Manager",
      icon: Users,
      iconBg: "#FBE6E4",
      title: "Total orders:",
      content: groupOrders
        ? `${groupOrders?.joined_member_count} out of ${groupOrders?.head_count} joined`
        : "loading...",
    },
    {
      id: 4,
      type: "Manager",
      icon: Users,
      iconBg: "#FBE6E4",
      title: "Total Estimated Cost:",
      content: `${groupOrders?.tax_info?.currency || ""}${
        (groupOrders?.per_person_budget?.cents / 100 || 0) *
          groupOrders?.head_count || 0
      }`,
    },
    {
      id: 5,
      type: "Manager",
      icon: Clock,
      iconBg: "#FDEBD7",
      title: "Subsidy",
      content: `${groupOrders?.per_person_budget ? perPersonBudget : 0}/person`,
    },
  ];
  const userData = [
    {
      id: 6,
      type: "User",
      icon: Coins,
      iconBg: "#FDEBD7",
      title: "Total Subsidy Spent:",
      content: (groupOrders?.tax_info?.currency || "") + (consumed_budget / 100 || 0),
    },
    {
      id: 7,
      type: "User",
      icon: CreditCard,
      iconBg: "#E3F3E5",
      title: "Subsidy:",
      content: groupOrders?.per_person_budget ? perPersonBudget : 0,
    },
    {
      id: 9,
      type: "User",
      icon: Users,
      iconBg: "#FBE6E4",
      title: "Total Paid by You:",
      content: (groupOrders?.tax_info?.currency || "") + (totalPaidByEmployees || 0),
    },
  ];

  const defaultData = [
    {
      id: 11,
      type: "Default",
      icon: Coins,
      iconBg: "#FDEBD7",
      title: "Top Up:",
      content: (groupOrders?.tax_info?.currency || "") + (total_topup / 100 || 0),
    },
    {
      id: 12,
      type: "Default",
      icon: CreditCard,
      iconBg: "#E3F3E5",
      title: "Budget Left:",
      content: groupOrders?.per_person_budget
        ? (groupOrders?.tax_info?.currency || "") + outsideFromBudget
        : "loading...",
    },
    {
      id: 13,
      type: "Default",
      icon: Clock,
      iconBg: "#FDEBD7",
      title: "Order must be submitted by:",
      content: (
        <>
          {isToday && <span>(TODAY) </span>}
          {formattedDate}
        </>
      ),
    },
    {
      id: 14,
      type: "Default",
      icon: Users,
      iconBg: "#FBE6E4",
      title: "Group:",
      content: groupOrders
        ? `${groupOrders?.joined_member_count} out of ${groupOrders.head_count} joined`
        : "loading...",
    },
  ];

  return (
    <div className="invitation__block">
      <div className="container">
        <div className="invitation__head">
          <div className="invitation__head-logo">
            <ImagePreview
              image={groupOrders?.preset?.chefs[0]?.profile_image_url}
              alt={presets?.name || "restaurant"}
            />
          </div>
          <div className="invitation__head-body">
            <h2>{presets?.name ? presets.name : "loading..."}</h2>
            {presets?.short_address ? (
              <p className="invitation__head-location">
                <MapPin size={14} />
                {presets.short_address}
              </p>
            ) : null}
            {presets?.cuisine_types?.length ? (
              <div className="invitation__head-tags">
                {presets.cuisine_types.map((tag) => (
                  <span key={tag}>{tag}</span>
                ))}
              </div>
            ) : null}
          </div>
          <div
            className="invitation__head-banner"
            style={{
              background: `URL("${groupOrders?.preset?.banner_image_url}") center center / cover no-repeat`,
            }}
          />
          <a
            className="invitation__head-avatar"
            href={
              groupOrders?.preset?.shared_url
                ? groupOrders?.preset?.shared_url
                : "/"
            }
          >
            <ImagePreview
              image={groupOrders?.preset?.chefs[0]?.profile_image_url}
              alt="invititation"
            />
          </a>
        </div>
        <div className="invitation__subhead">
          <span>{groupOrders?.name}</span>
          <p>
            <span className="invitation__clock" aria-hidden="true">
              🕐
            </span>
            {groupOrders?.delivery_date_time
              ? format(
                  new Date(groupOrders.delivery_date_time * 1000),
                  "EEEE, MMMM do, h:mm aaa"
                )
              : "loading..."}
          </p>
        </div>
        {/** Manager View */}
        {hideEdit ? (
          isManager ? (
            <div className="orders__info">
              <div className="orders__left">
                {/*** Yohooooo */}
                {managerData.map((order, index) => (
                  <React.Fragment key={order.id}>
                    <OrderInformation
                      icon={order.icon}
                      iconBg={order.iconBg}
                      title={order.title}
                      content={order.content}
                    />
                    {/* {index !== managerData.length - 1 && (
                        <div className="spacer" />
                      )} */}
                  </React.Fragment>
                ))}
              </div>
              <ShareIcon shareLink={shareLink} />
            </div>
          ) : (
            // UI for Regular Users when hideEdit is true "USER"
            <div className="orders__info">
              <div className="orders__left">
                {/*** Yohooooo */}
                {userData.map((order, index) => (
                  <React.Fragment key={order.id}>
                    <OrderInformation
                      icon={order.icon}
                      iconBg={order.iconBg}
                      title={order.title}
                      content={order.content}
                    />
                  </React.Fragment>
                ))}
              </div>
              <div className="orders__right">
                <button type="button" onClick={shareLink} className="share-link-btn">
                  <Link2 size={16} />
                  Share link
                </button>
              </div>
            </div>
          )
        ) : (
          // UI when hideEdit is false (normal UI)
          <div className="orders__info">
            <div className="orders__left">
              {defaultData.map((order, index) => (
                <React.Fragment key={order.id}>
                  <OrderInformation
                    icon={order.icon}
                    iconBg={order.iconBg}
                    title={order.title}
                    content={order.content}
                  />
                  {/* {index !== managerData.length - 1 && (
                        <div className="spacer" />
                      )} */}
                </React.Fragment>
              ))}
            </div>
            <div className="orders__right">
              <button type="button" onClick={shareLink} className="share-link-btn">
                <Link2 size={16} />
                Share link
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default Invitation;
